import { Action, Middleware, StateFromReducersMapObject } from 'redux';
import { configureStore as createStore } from '@reduxjs/toolkit';
import { ThunkDispatch } from 'redux-thunk';
import { setupListeners } from '@reduxjs/toolkit/query';
import { PersistConfig, PersistedState, persistCombineReducers } from 'redux-persist';
import { coreApi } from '@cp/shared/api/core';
import { createDialogMiddleware } from '@cp/entities/dialog';
import { isBrowser } from '@cp/utils/browser';
import { IoContainer } from '@cp/shared/di';
import { reducerConfig } from './slices';

interface ConfigureStoreOptions<S = unknown> {
  container: IoContainer;
  preloadedState?: S;
  persistConfig: PersistConfig<S>;
}

export const configureStore = ({
  preloadedState,
  persistConfig,
  container,
}: ConfigureStoreOptions<StateFromReducersMapObject<typeof reducerConfig> & PersistedState>) => {
  const browserMiddlewares: Middleware[] = isBrowser ? [createDialogMiddleware({ container })] : [];
  const middlewares: Middleware[] = [...browserMiddlewares];

  const store = createStore({
    reducer: persistCombineReducers(persistConfig, reducerConfig as any),
    preloadedState,
    middleware: (gDM) => gDM({ serializableCheck: false, immutableCheck: isBrowser }).prepend(middlewares).concat(coreApi.middleware),
    devTools: isBrowser,
  });

  if (isBrowser) {
    setupListeners(store.dispatch);
  }

  return store as typeof store & { dispatch: ThunkDispatch<ReturnType<typeof store.getState>, void, Action> };
};

/**
 * Store typings
 */
export type StoreType = ReturnType<typeof configureStore>;
export type RootState = ReturnType<StoreType['getState']>;
